import type {
  AdminPassportTransfer,
  PassportTransfer,
  TransferReceiptCode,
  TransferReceiptSession,
  TransferReceiptSessionStatus,
  TransferSide,
  TransferVisibilityChoice,
  TransferVisibilityRequest,
} from '@/types';
import { httpClient } from './httpClient';

const BASE = '/api/art';
const PASSPORTS = `${BASE}/passports`;
const ADMIN = `${BASE}/admin/transfers`;

function transferUrl(id: string, tail = ''): string {
  return `${PASSPORTS}/transfers/${encodeURIComponent(id)}${tail}`;
}

function adminUrl(id: string, tail = ''): string {
  return `${ADMIN}/${encodeURIComponent(id)}${tail}`;
}

export async function startTransfer(
  passportId: string,
  recipientEmail: string,
  headers: Record<string, string>,
): Promise<PassportTransfer> {
  const response = await httpClient.post<PassportTransfer>(
    `${PASSPORTS}/${encodeURIComponent(passportId)}/transfers`,
    { recipient_email: recipientEmail },
    { headers },
  );

  return response.data;
}

export async function listTransfers(side: TransferSide): Promise<PassportTransfer[]> {
  const response = await httpClient.get<PassportTransfer[]>(`${PASSPORTS}/transfers`, {
    params: { side },
  });

  return response.data;
}

export async function getTransfer(id: string): Promise<PassportTransfer> {
  const response = await httpClient.get<PassportTransfer>(transferUrl(id));

  return response.data;
}

export async function acceptTransfer(id: string, token?: string): Promise<PassportTransfer> {
  const response = await httpClient.post<PassportTransfer>(transferUrl(id, '/accept'), {
    token: token ?? '',
  });

  return response.data;
}

export async function declineTransfer(id: string): Promise<PassportTransfer> {
  const response = await httpClient.post<PassportTransfer>(transferUrl(id, '/decline'));

  return response.data;
}

export async function cancelTransfer(id: string): Promise<PassportTransfer> {
  const response = await httpClient.post<PassportTransfer>(transferUrl(id, '/cancel'));

  return response.data;
}

export async function startReceiptSession(id: string): Promise<TransferReceiptSession> {
  const response = await httpClient.post<TransferReceiptSession>(
    transferUrl(id, '/receipt-sessions'),
  );

  return response.data;
}

export async function getReceiptSession(
  id: string,
  sessionId: string,
): Promise<TransferReceiptSessionStatus> {
  const response = await httpClient.get<TransferReceiptSessionStatus>(
    transferUrl(id, `/receipt-sessions/${encodeURIComponent(sessionId)}`),
  );

  return response.data;
}

// confirmReceiptCode sends the code from the tag tap page and the new owner's PIN in one request.
export async function confirmReceiptCode(
  id: string,
  sessionId: string,
  code: string,
  pin: string,
): Promise<PassportTransfer> {
  const response = await httpClient.post<PassportTransfer>(
    transferUrl(id, `/receipt-sessions/${encodeURIComponent(sessionId)}/confirm`),
    { code, pin },
  );

  return response.data;
}

// revealReceiptCode is called by the tag scan page. It needs no login: the scan token is the proof.
export async function revealReceiptCode(vt: string): Promise<TransferReceiptCode> {
  const response = await httpClient.post<TransferReceiptCode>(
    `${PASSPORTS}/transfers/receipt-sessions/reveal`,
    { vt },
  );

  return response.data;
}

export async function reportProblem(id: string, note: string): Promise<PassportTransfer> {
  const response = await httpClient.post<PassportTransfer>(transferUrl(id, '/report-problem'), {
    note,
  });

  return response.data;
}

export async function setTransferVisibility(
  id: string,
  body: TransferVisibilityRequest,
): Promise<TransferVisibilityChoice> {
  const response = await httpClient.post<TransferVisibilityChoice>(
    transferUrl(id, '/provenance-visibility'),
    body,
  );

  return response.data;
}

export async function adminGetTransfer(id: string): Promise<AdminPassportTransfer> {
  const response = await httpClient.get<AdminPassportTransfer>(adminUrl(id));

  return response.data;
}

export async function adminConfirmReceipt(
  id: string,
  reason: string,
): Promise<AdminPassportTransfer> {
  const response = await httpClient.post<AdminPassportTransfer>(adminUrl(id, '/confirm-receipt'), {
    reason,
  });

  return response.data;
}

export async function adminCancelTransfer(
  id: string,
  reason: string,
): Promise<AdminPassportTransfer> {
  const response = await httpClient.post<AdminPassportTransfer>(adminUrl(id, '/cancel'), {
    reason,
  });

  return response.data;
}
